import createReferenceAttachmentFromAttachmentDataProviderService from '../services/createReferenceAttachmentFromAttachmentDataProvider';
import type CreateAttachmentResult from '../types/CreateAttachmentResult';
import { getErrorForAttachmentCreationFailure } from '../utils/getErrorForAttachmentCreationFailure';
import { wrapFunctionForDatapoint } from 'owa-analytics';
import type { CloudFile } from 'owa-attachment-file-types';
import type { ClientItemId } from 'owa-client-ids';
import type AttachmentInfoResponseMessage from 'owa-service/lib/contract/AttachmentInfoResponseMessage';
import {
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isStringNullOrWhiteSpace } from 'owa-localize';
import { lazyRemediateAuth } from 'owa-auth-redemption';

export default wrapFunctionForDatapoint(
    {
        name: 'AttachmentCreationCloudyFromAttachmentDataProvider',
        customData: (
            parentItemId: ClientItemId,
            cancellationId: string,
            backingObject: CloudFile,
            attachmentState: AttachmentState,
            isRetryForAuthContext?: boolean
        ) => [backingObject.providerType, backingObject.fileType],
    },
    async function createReferenceAttachmentFromAttachmentDataProvider(
        parentItemId: ClientItemId,
        cancellationId: string,
        backingObject: CloudFile,
        attachmentState: AttachmentState,
        isRetryForAuthContext?: boolean
    ): Promise<CreateAttachmentResult> {
        if (attachmentState.attachmentType === AttachmentStateType.Attachment) {
            startFakeProgressIndicator(
                4000, // For createReferenceAttachmentFromAttachmentDataProvider the calltime does not depend on file size
                // 4000 was choosen to give a smooth progress bar for the average user.
                0.05, // Update the progress bar about every fifth of a second for a smooth bar (4000ms*.05 = 200ms = .2 seconds).
                (completePercent: number) => {
                    setActionCompletePercent(
                        <AttachmentFullViewState>attachmentState,
                        completePercent
                    );
                },
                attachmentState.attachmentId
            );
        }

        return createReferenceAttachmentFromAttachmentDataProviderService(
            parentItemId,
            cancellationId,
            backingObject
        ).then(async createAttachmentResponse => {
            const responseMessage: AttachmentInfoResponseMessage =
                createAttachmentResponse.ResponseMessages.Items.length > 0
                    ? createAttachmentResponse.ResponseMessages.Items[0]
                    : null;
            if (responseMessage && responseMessage.Attachments.length > 0) {
                stopFakeProgressIndicator(attachmentState.attachmentId, true);
                const result: CreateAttachmentResult = {
                    attachment: responseMessage.Attachments[0],
                    sharingInfo: createAttachmentResponse.SharingInformation,
                };
                return result;
            } else {
                stopFakeProgressIndicator(attachmentState.attachmentId, false);

                // Check claims challenge and show dialog
                if (isFeatureEnabled('doc-authContext')) {
                    if (
                        !isRetryForAuthContext &&
                        createAttachmentResponse.SharingInformation &&
                        !isStringNullOrWhiteSpace(
                            createAttachmentResponse.SharingInformation.ClaimsChallenge
                        )
                    ) {
                        const authenticateHeader: string =
                            createAttachmentResponse.SharingInformation.ClaimsChallenge;

                        if (!isStringNullOrWhiteSpace(authenticateHeader)) {
                            try {
                                const remediateAuth = await lazyRemediateAuth.import();
                                await remediateAuth(authenticateHeader);
                            } catch (e) {
                                // fail to re-auth
                                throw getErrorForAttachmentCreationFailure(
                                    responseMessage,
                                    createAttachmentResponse.SharingInformation.ResultCode.toString()
                                );
                            }

                            // retry
                            return createReferenceAttachmentFromAttachmentDataProvider(
                                parentItemId,
                                cancellationId,
                                backingObject,
                                attachmentState,
                                true /* isRetryForAuthContext */
                            );
                        }
                    }
                }

                throw getErrorForAttachmentCreationFailure(responseMessage);
            }
        });
    }
);
